import React, { useRef, useEffect, useState } from "react";
import { Bot, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { ToolCardShell, type CardStatus } from "../tool";
import { ToolInput, ToolOutput } from "../tool";
import type { ToolCardComponentProps } from "../tool-registry";

interface SubagentToolCall {
  tool_call_id?: string;
  tool_name: string;
  status: "detected" | "executing" | "success" | "failed";
  tool_args?: Record<string, unknown>;
  result?: unknown;
  error?: string;
  timestamp: string;
}

interface SubagentInvocationData {
  subagentName: string;
  task?: string;
  status: "invoking" | "working" | "completed" | "error";
  result?: string;
  error?: string;
  executionTime?: number;
  timestamp: string;
  streamContent?: string;
  toolCalls?: SubagentToolCall[];
  messages?: Array<{ role: string; content?: string; timestamp: string }>;
}

function mapSubagentStatus(status: string): CardStatus {
  switch (status) {
    case "invoking":
    case "working":
      return "executing";
    case "completed":
      return "success";
    case "error":
      return "failed";
    default:
      return "detected";
  }
}

function formatExecutionTime(seconds: number): string {
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
}

function truncate(text: string, maxLen: number): string {
  return text.length > maxLen ? text.slice(0, maxLen) + "..." : text;
}

const StepStatusIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case "executing":
      return <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />;
    case "success":
      return <CheckCircle2 className="h-3.5 w-3.5 text-success" />;
    case "failed":
      return <XCircle className="h-3.5 w-3.5 text-destructive" />;
    default:
      return <span className="h-3.5 w-3.5 rounded-full border border-muted-foreground/30" />;
  }
};

interface SubagentCardInnerProps {
  subagentName: string;
  task: string;
  cardStatus: CardStatus;
  streamContent: string;
  toolCalls: SubagentToolCall[];
  executionTime?: number;
  error?: string;
  args?: Record<string, unknown>;
  result?: unknown;
}

const SubagentCardInner: React.FC<SubagentCardInnerProps> = ({
  subagentName,
  task,
  cardStatus,
  streamContent,
  toolCalls,
  executionTime,
  error,
  args,
  result,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [needsCollapse, setNeedsCollapse] = useState(false);
  const isExecuting = cardStatus === "executing";

  useEffect(() => {
    if (contentRef.current) {
      setNeedsCollapse(contentRef.current.scrollHeight > 80);
    }
  }, [streamContent, toolCalls]);

  const hasContent = streamContent.length > 0 || toolCalls.length > 0;

  return (
    <ToolCardShell
      toolName={subagentName || "子智能体"}
      status={cardStatus}
      icon={<Bot className="h-4 w-4" />}
      iconColor="text-teal-500"
      defaultExpanded={isExecuting}
    >
      <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        {task && <span className="truncate max-w-full">{truncate(task, 80)}</span>}
        {toolCalls.length > 0 && (
          <span className="inline-flex items-center rounded-full bg-teal-100 px-2 py-0.5 text-teal-700">
            {toolCalls.length} 工具
          </span>
        )}
      </div>

      {hasContent && (
        <div ref={contentRef} className="relative">
          {streamContent.length > 0 && (
            <div className="mx-4 mb-2 text-xs text-muted-foreground whitespace-pre-wrap font-mono bg-muted/50 rounded p-2 max-h-[40lh] overflow-y-auto">
              {streamContent}
            </div>
          )}

          {toolCalls.length > 0 && (
            <div className="px-4 py-2 space-y-0">
              {toolCalls.map((tc, idx) => (
                <div key={tc.tool_call_id || `${tc.tool_name}-${idx}`} className="flex items-start gap-2">
                  <div className="flex flex-col items-center">
                    <StepStatusIcon status={tc.status} />
                    {idx < toolCalls.length - 1 && (
                      <div className="w-px h-4 bg-border" />
                    )}
                  </div>
                  <span className="text-xs text-foreground py-0.5">{tc.tool_name}</span>
                </div>
              ))}
            </div>
          )}

          {needsCollapse && (
            <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-background to-transparent pointer-events-none" />
          )}
        </div>
      )}

      {args && Object.keys(args).length > 0 && <ToolInput data={args} />}
      {(result !== undefined && result !== null) || error ? (
        <ToolOutput data={result} error={error} />
      ) : null}

      {executionTime != null && cardStatus === "success" && (
        <div className="px-4 py-2 border-t border-border/50 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>{formatExecutionTime(executionTime)}</span>
        </div>
      )}
    </ToolCardShell>
  );
};

export const SubagentCard: React.FC<ToolCardComponentProps> = ({ toolCall }) => {
  const args = toolCall.args || {};
  const cardStatus: CardStatus = toolCall.status || "detected";
  const result = toolCall.result;

  const subagentName = String(args.subagent_name || args.agent_name || "子智能体");
  const task = String(args.task || args.description || "");
  const streamContent =
    typeof result === "string"
      ? result
      : typeof result === "object" && result !== null
        ? String((result as Record<string, unknown>).streamContent || (result as Record<string, unknown>).content || "")
        : "";

  let toolCalls: SubagentToolCall[] = [];
  if (typeof result === "object" && result !== null) {
    const r = result as Record<string, unknown>;
    if (Array.isArray(r.toolCalls)) toolCalls = r.toolCalls;
    else if (Array.isArray(r.tool_calls)) toolCalls = r.tool_calls;
  }

  const executionTime =
    typeof result === "object" && result !== null
      ? (result as Record<string, unknown>).executionTime as number | undefined
      : undefined;

  return (
    <SubagentCardInner
      subagentName={subagentName}
      task={task}
      cardStatus={cardStatus}
      streamContent={streamContent}
      toolCalls={toolCalls}
      executionTime={executionTime}
      error={toolCall.error}
      args={args}
      result={result}
    />
  );
};

export interface SubagentCardFromInvocationProps {
  data: SubagentInvocationData;
}

export const SubagentCardFromInvocation: React.FC<SubagentCardFromInvocationProps> = ({ data }) => {
  const cardStatus = mapSubagentStatus(data.status);
  const streamContent = data.streamContent || "";
  const toolCalls = data.toolCalls || [];

  return (
    <SubagentCardInner
      subagentName={data.subagentName || "子智能体"}
      task={data.task || ""}
      cardStatus={cardStatus}
      streamContent={streamContent}
      toolCalls={toolCalls}
      executionTime={data.executionTime}
      error={data.error}
    />
  );
};

export type { SubagentInvocationData, SubagentToolCall };
